/**
 * Shared formatting helpers for the Admin vnext surface.
 *
 * @remarks
 * These helpers intentionally keep UI formatting decisions local to the vnext
 * substrate so migrated routes stop depending on legacy page presentation
 * helpers while preserving stable user-facing value formatting.
 *
 * Dates are formatted for a United Kingdom and New Zealand audience with an
 * explicit locale and an explicit month name. Leaving the locale to the
 * browser produced US `3/17/2027` on staff machines, and a bare numeric date
 * is ambiguous between those conventions whichever way it renders.
 */

const VNEXT_DATE_LOCALE = "en-GB";

const VNEXT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

const VNEXT_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
};

export const formatVNextDateTime = (value: string | null | undefined) => {
  if (!value) {
    return "Not recorded";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Invalid date";
  }

  // Seconds are noise on an audit line and never the thing being read.
  return parsed.toLocaleString(VNEXT_DATE_LOCALE, {
    ...VNEXT_DATE_OPTIONS,
    ...VNEXT_TIME_OPTIONS,
  });
};

export const formatVNextDate = (value: string | null | undefined) => {
  if (!value) {
    return "Not recorded";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Invalid date";
  }

  return parsed.toLocaleDateString(VNEXT_DATE_LOCALE, VNEXT_DATE_OPTIONS);
};

export const formatVNextNumber = (value: number | null | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "0";
  }

  return value.toLocaleString("en-US");
};

export const formatVNextCurrency = (
  amountMinorUnits: number | null | undefined,
  currency = "USD",
) => {
  if (typeof amountMinorUnits !== "number" || Number.isNaN(amountMinorUnits)) {
    return "n/a";
  }

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amountMinorUnits / 100);
  } catch {
    return `${currency} ${formatVNextNumber(amountMinorUnits / 100)}`;
  }
};

export const formatVNextLabel = (value: string | null | undefined) => {
  if (!value) {
    return "n/a";
  }

  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatMaskedProviderReference = (
  value: string | null | undefined,
): string => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) {
    return "Not available";
  }

  if (trimmedValue.length <= 4) {
    return "Ref masked";
  }

  return `Ref ending ${trimmedValue.slice(-4)}`;
};
